import { useCallback, useEffect, useRef } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import * as Sentry from "@sentry/react";
import { authApi } from "@/lib/api";
import { onAuthTokensChanged, tokenStorage } from "@/lib/auth";
import { stytchClient } from "@/lib/stytch";
import type { User } from "@/types";
import { DEV_USER, isDevAuthBypass } from "@/lib/dev-mode";
import { useOrganizationStore } from "@/stores/organizationStore";
import { useFeatureFlagIdentity } from "@/hooks/useFeatureFlags";

export const useAuth = () => {
  const queryClient = useQueryClient();
  const { identify: identifyUser, reset: resetIdentity } = useFeatureFlagIdentity();

  useEffect(() => {
    return onAuthTokensChanged(() => {
      const accessToken = tokenStorage.getAccessToken();
      const refreshToken = tokenStorage.getRefreshToken();

      if (!accessToken && !refreshToken) {
        Sentry.setUser(null);
        resetIdentity();
        useOrganizationStore.getState().clearSelection();
        queryClient.setQueryData(["user"], null);
        return;
      }

      queryClient.invalidateQueries({ queryKey: ["user"] });
    });
  }, [queryClient, resetIdentity]);

  const stytchLoginMutation = useMutation({
    mutationFn: async ({
      sessionToken,
      sessionJwt,
    }: {
      sessionToken: string;
      sessionJwt?: string;
    }) => {
      const response = await authApi.stytchExchange(sessionToken, sessionJwt);
      tokenStorage.set(response);
      return response;
    },
  });

  // Ref keeps mutateAsync stable so stytchLogin's useCallback dep list stays minimal.
  const stytchLoginMutateRef = useRef(stytchLoginMutation.mutateAsync);
  stytchLoginMutateRef.current = stytchLoginMutation.mutateAsync;

  const userQuery = useQuery({
    queryKey: ["user"],
    queryFn: async (): Promise<User | null> => {
      // Dev mode: serve a fake user so the app runs with no backend.
      if (isDevAuthBypass()) return DEV_USER;

      const accessToken = tokenStorage.getAccessToken();
      const refreshToken = tokenStorage.getRefreshToken();

      if (!accessToken && !refreshToken) {
        return null;
      }

      try {
        const user = await authApi.getCurrentUser();
        Sentry.setUser({
          id: user?.id,
          email: user?.email,
        });
        if (user) {
          // Identify for feature-flag targeting.
          identifyUser(user.id, {
            email: user.email,
            is_staff: user.is_staff,
            organization_ids: user.organizations.map((org) => org.id),
          });
        }
        return user;
      } catch (error: any) {
        if (error.status === 401) {
          return null;
        }
        throw error;
      }
    },
    retry: (failureCount, error: any) => {
      if (error?.status === 401) return false;
      return failureCount < 3;
    },
    staleTime: 1000 * 60 * 5,
  });

  const logout = async () => {
    try {
      await stytchClient.session.revoke({ forceClear: true });
    } finally {
      tokenStorage.remove();
      useOrganizationStore.getState().clearSelection();
      queryClient.clear();
    }
  };

  const stytchLogin = useCallback(
    async (sessionToken: string, sessionJwt?: string) => {
      const result = await stytchLoginMutateRef.current({ sessionToken, sessionJwt });
      // Wait for the user query to populate before returning so callers can
      // navigate immediately without seeing a flash of isAuthenticated=false.
      await queryClient.refetchQueries({ queryKey: ["user"] });
      return result;
    },
    [queryClient],
  );

  return {
    user: userQuery.data,
    isLoading: userQuery.isLoading,
    isError: userQuery.isError,
    isAuthenticated: !!userQuery.data,
    stytchLogin,
    logout,
  };
};
